/**
 * statementCapabilities — per-engine SQL-statement capability truth table.
 * Mirrors the `render/select/setOperatorKindMaps.ts` pattern: a small, pure,
 * engine-keyed lookup with no React/Electron imports, unit-tested in
 * isolation, that a renderer consults to decide what's honest to show.
 *
 * First (and so far only) consumer: MERGE (VQE Polish EDG-01/02/03/04/29).
 * MSSQL and Oracle have always supported MERGE. PostgreSQL gained it in v15.
 * MySQL and SQLite have no MERGE statement at all — there is no version that
 * adds it, so `mergeMinVersionHint` only ever returns a value for postgres.
 *
 * `ParseOptions.serverVersion` isn't reliably populated for VQE parses today,
 * so this module ships the boolean capability plus a static "added in vNN"
 * UI hint instead of gating on an actually-detected connection version (the
 * same shortcut the bug dossier's BUG 3(a) takes for named-window gating) —
 * see `Ralph/VqePolish/findings/FINDINGS-CHECKLIST.md` EDG-29.
 */

import { ENGINE_REGISTRY } from '../../../../core/engines/engineDefinitions';
import { sqlEngineToDatabaseEngineId } from '../../../../core/engines/sqlEngineToDatabaseEngineId';
import type { SqlEngine } from '../../../sql-grammar/model';

/**
 * Dialect-correct product name for capability copy — e.g. "MySQL", not the
 * internal `mysql` engine id, and "SQL Server" (not "MSSQL") for `mssql`.
 *
 * Composed from two existing pure, process-agnostic single sources of truth
 * rather than hand-rolled here: `sqlEngineToDatabaseEngineId` maps the
 * grammar-layer `SqlEngine` to the connection-store `DatabaseEngineId`
 * (`postgres` → `pgsql`), then `ENGINE_REGISTRY[...].display.shortName`
 * supplies the product name.
 */
function engineDisplayName(engine: SqlEngine): string {
  return ENGINE_REGISTRY[sqlEngineToDatabaseEngineId(engine)].display.shortName;
}

export const ENGINE_DISPLAY_NAME: Record<SqlEngine, string> = {
  mssql: engineDisplayName('mssql'),
  postgres: engineDisplayName('postgres'),
  mysql: engineDisplayName('mysql'),
  oracle: engineDisplayName('oracle'),
  sqlite: engineDisplayName('sqlite'),
};

interface MergeCapability {
  readonly supported: boolean;
  /**
   * Minimum version that introduced MERGE, for engines whose support is
   * version-gated. Only postgres carries one today — MSSQL/Oracle have
   * always had MERGE, and MySQL/SQLite never will, so neither has a
   * meaningful version number to name.
   */
  readonly minVersion?: string;
}

const MERGE_CAPABILITY: Record<SqlEngine, MergeCapability> = {
  mssql: { supported: true },
  oracle: { supported: true },
  postgres: { supported: true, minVersion: '15' },
  mysql: { supported: false },
  sqlite: { supported: false },
};

/** Whether `engine` supports the MERGE statement at all (EDG-01/02/03/04). */
export function supportsMerge(engine: SqlEngine): boolean {
  return MERGE_CAPABILITY[engine].supported;
}

/**
 * A UI-only version hint for engines whose MERGE support is version-gated
 * (postgres 15+ today). `undefined` for engines where MERGE always works
 * (mssql/oracle) or never does (mysql/sqlite) — there's no version number to
 * name in either case (EDG-29).
 */
export function mergeMinVersionHint(engine: SqlEngine): string | undefined {
  return MERGE_CAPABILITY[engine].minVersion;
}

/**
 * Named `WINDOW w AS (…)` clause support is version-gated on SQL Server (2022)
 * and Oracle (21c); PostgreSQL, MySQL (8.0), and SQLite have had it long enough
 * that no honest version hint is warranted (ANA-01/02). Like
 * `mergeMinVersionHint`, this ships a static "added in vNN" UI hint rather than
 * gating on a detected connection version (`ParseOptions.serverVersion` isn't
 * reliably populated for VQE parses) — so it stays a truthful hint, never a
 * false "unsupported" block. `undefined` for engines that need no hint.
 */
const WINDOW_CLAUSE_MIN_VERSION: Partial<Record<SqlEngine, string>> = {
  mssql: '2022',
  oracle: '21c',
};

export function windowClauseMinVersionHint(engine: SqlEngine): string | undefined {
  return WINDOW_CLAUSE_MIN_VERSION[engine];
}
